import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { Sidebar } from 'primereact/sidebar';
import { Toast } from 'primereact/toast';
import { SortOrder } from 'primereact/api';
import { useUsers } from '../hooks/useUsers';
import UserForm from '../components/UserForm';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { TableWithHeader, TableColumn } from '../../common';
import { User, CreateUserRequest, UpdateUserRequest } from '../types/users.types';
import { UserRole } from '../../auth/types/auth.types';
import { RoleService } from '../../layouts/services/role.service';
import { AlcanceMetasSection } from '../../metas-colaborador/components/AlcanceMetasSection';

const UsersScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    users,
    currentUser,
    isLoading,
    formLoading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    setCurrentUser,
    clearCurrentUser,
    clearError,
  } = useUsers();

  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [sortField, setSortField] = useState<keyof User>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>(-1);
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (error) {
      toast?.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 3000,
      });
      clearError();
    }
  }, [error, toast, clearError]);

  const handleCreateUser = () => {
    setIsEditMode(false);
    clearCurrentUser();
    setSidebarVisible(true);
  };

  const handleEditUser = (user: User) => {
    setIsEditMode(true);
    setSidebarVisible(true);
    setCurrentUser(user);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteModalVisible(true);
  };

  const handleFormSubmit = async (data: CreateUserRequest | UpdateUserRequest) => {
    try {
      if (isEditMode && currentUser) {
        await updateUser(currentUser.id, data as UpdateUserRequest);
        toast?.show({
          severity: 'success',
          summary: 'Exito',
          detail: 'Usuario actualizado correctamente',
          life: 3000,
        });
      } else {
        await createUser(data as CreateUserRequest);
        toast?.show({
          severity: 'success',
          summary: 'Exito',
          detail: 'Usuario creado correctamente',
          life: 3000,
        });
      }
      setSidebarVisible(false);
      clearCurrentUser();
      fetchUsers();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleFormCancel = () => {
    setSidebarVisible(false);
    clearCurrentUser();
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      try {
        await deleteUser(userToDelete.id);
        toast?.show({
          severity: 'success',
          summary: 'Exito',
          detail: 'Usuario eliminado correctamente',
          life: 3000,
        });
        setDeleteModalVisible(false);
        setUserToDelete(null);
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setUserToDelete(null);
  };

  const getRoleSeverity = (role: string) => {
    switch (role) {
      case 'SUPERADMIN':
        return 'danger';
      case 'ADMIN':
        return 'danger';
      case 'MANAGER':
        return 'warning';
      case 'BRANCH_OFFICE':
        return 'info';
      case 'SELLER':
        return 'success';
      case 'WAREHOUSE_STAFF':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatDate = (date: Date | string) => {
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString();
    }
    return date.toLocaleDateString();
  };

  const actionBodyTemplate = (rowData: User) => {
    return (
      <div className="flex space-x-2">
        <button
          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
          title="Editar usuario"
          onClick={() => handleEditUser(rowData)}
        >
          <i className="pi pi-pencil text-sm"></i>
        </button>
        <button
          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
          title="Eliminar usuario"
          onClick={() => handleDeleteUser(rowData)}
        >
          <i className="pi pi-trash text-sm"></i>
        </button>
      </div>
    );
  };

  const roleBodyTemplate = (rowData: User) => (
    <Tag 
      value={rowData.roleDisplay || rowData.role} 
      severity={getRoleSeverity(rowData.role)}
      className="capitalize"
    />
  );

  const dateBodyTemplate = (rowData: User) => (
    <span>{formatDate(rowData.createdAt)}</span>
  );

  const columns: TableColumn<User>[] = [
    {
      field: 'email',
      header: 'Correo electrónico',
      sortable: true,
      minWidth: '200px',
    },
    {
      field: 'name',
      header: 'Nombre',
      sortable: true,
      minWidth: '120px',
    },
    {
      field: 'branchOffice',
      header: 'Sucursal',
      sortable: true,
      minWidth: '120px',
    },
    {
      field: 'employeeNumber',
      header: 'No. empleado',
      sortable: true,
      minWidth: '110px',
    },
    {
      field: 'roleDisplay',
      header: 'Rol',
      sortable: true,
      body: roleBodyTemplate,
      minWidth: '140px',
    },
    {
      field: 'createdAt',
      header: 'Creado el',
      sortable: true,
      body: dateBodyTemplate,
      minWidth: '120px',
    },
    {
      field: 'id',
      header: 'Acciones',
      body: actionBodyTemplate,
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <Toast ref={setToast} />
      
      <TableWithHeader
        data={users}
        columns={columns}
        loading={isLoading}
        header={{
          title: 'Gestión de usuarios',
          subtitle: 'Gestiona los usuarios del sistema y sus permisos',
          actionButton: {
            icon: 'pi pi-plus',
            label: 'Crear usuario',
            onClick: handleCreateUser,
          },
          secondaryButton: {
            icon: 'pi pi-upload',
            label: 'Importar usuarios',
            onClick: () => navigate('/users/import'),
          },
        }}
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={setGlobalFilterValue}
        globalFilterPlaceholder="Buscar usuarios..."
        globalFilterFields={['email', 'userCode', 'roleDisplay', 'branchOffice', 'employeeNumber']}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(field, order) => {
          setSortField(field);
          setSortOrder(order);
        }}
        emptyMessage="No se encontraron usuarios"
      />

      {/* Sidebar for Create/Edit Form */}
      <Sidebar
        visible={sidebarVisible}
        position="right"
        onHide={handleFormCancel}
        className="w-full md:w-6 lg:w-5 xl:w-4"
        style={{ width: '35rem' }}
        dismissable={false}
      >
        <div className="p-6">
          <UserForm
            user={isEditMode ? currentUser : undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            isLoading={formLoading}
          />
        </div>
        {/* Alcance de metas (supervisor / regional): sólo cuentas Indicadores y
            sólo para administradores, que es a quien la API se lo permite. Va
            FUERA de UserForm: se guarda al instante contra otra ruta. */}
        {isEditMode && currentUser && currentUser.role === UserRole.INDICADORES && RoleService.isAdmin() && (
          <AlcanceMetasSection usuario={{ id: currentUser.id, email: currentUser.email }} />
        )}
      </Sidebar>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        user={userToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default UsersScreen; 